import DownloadPopup from "components/core/modal-components/DownloadPopup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CF_URLS } from "services/api/endpoints";
import { insertLoggedEvent } from "services/common";
import { selectAuth } from "store/slices";
import {
  ERROR_TYPES,
  GLOBAL_ATTRIBUTE_VALUES,
  SYSTEM_ID_SecureBase,
  VALUE_EMPTY_STRING,
  downloadOptionPdf,
  landscape,
  numberConstants
} from "utils/constants";
import { destructionPickReportComment, generateDestructionReportPrintDesc, operationTypes } from "utils/constants/logged-event/loggedEventConstants";
import {
  dataDogErrorLog,
  findErrorMessage,
  getGlobalAttributeValue,
  getUserName,
  handleRefId,
  printReport
} from "utils/helpers";

export default function DownloadReportModal({
  isVisible,
  setIsVisible,
  mainDistrictId,
  setIsLoading,
  districtName,
  systemId,
  languageLocaleId,
  destructRequestId,
  customerId,
  throwError,
  setThrowError,
  throwErrorMessage,
  setThrowErrorMessage,
  isPickPrint,
  setIsPickPrint,
  isPickSizeDisabled,
  selectedPickListSize,
  fetchDataExistingMDR,
  isReprint,
  pickListSizes
}) {
  const { auth } = useSelector(selectAuth);
  const { t } = useTranslation();
  const [reportCUrl, setReportCFUrl] = useState(VALUE_EMPTY_STRING);
  const [selectedValue, setSelectedValue] = useState(downloadOptionPdf);

  const fetchAttributeData = useCallback(async () => {
    const attributeData = await getGlobalAttributeValue(
      mainDistrictId,
      SYSTEM_ID_SecureBase,
      GLOBAL_ATTRIBUTE_VALUES.VALUE_189
    );
    return attributeData;
  }, [mainDistrictId]);
  useEffect(() => {
    const fetchData = async () => {
      const attributeData = await fetchAttributeData();
      const islandscape = attributeData === landscape;

      if (isVisible) {
        setReportCFUrl(
          isPickPrint
            ? islandscape
              ? CF_URLS.printReports.request.mdrVaultItemizedPickReport
              : CF_URLS.printReports.request.mdrVaultItemizedPickPotraitReport
            : CF_URLS.printReports.request.mdrVaultItemizedStdReport
        );
      } else {
        setReportCFUrl(VALUE_EMPTY_STRING);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const splitCount = useMemo(() => {
    if (isReprint) {
      return numberConstants.zero;
    }
    return isPickSizeDisabled
      ? pickListSizes[0].value || numberConstants.fiveHundred
      : selectedPickListSize?.value;
  }, [isReprint, isPickSizeDisabled, pickListSizes, selectedPickListSize]);

  //handle download
  const handleDownload = async () => {
    try {
      setIsLoading(true);
      setThrowError(false);

      const attributeData = await fetchAttributeData();
      const islandscape = attributeData === landscape;

      const reqBody = isPickPrint
        ? JSON.stringify({
          main_district_id: mainDistrictId,
          destruct_request_id: destructRequestId,
          logged_in_employee_id: auth?.user?.employee_id,
          islandscape: islandscape
            ? numberConstants.one
            : numberConstants.zero,
          split_destruct_picklist_value: splitCount,
          user_name: getUserName(auth?.user),
          report_format: selectedValue?.type?.toUpperCase()
        })
        : JSON.stringify({
          main_district_id: mainDistrictId,
          user_name: getUserName(auth?.user),
          destruct_request_id: destructRequestId,
          system_id: systemId,
          customer_id: customerId,
          language_locale_id: languageLocaleId,
          district_name: districtName,
          report_format: selectedValue?.type?.toUpperCase()
        });
      let reportResponse = await printReport(
        reqBody,
        isPickPrint
          ? islandscape
            ? `${CF_URLS.printReports.request.mdrVaultItemizedPickReport}`
            : `${CF_URLS.printReports.request.mdrVaultItemizedPickPotraitReport}`
          : `${CF_URLS.printReports.request.mdrVaultItemizedStdReport}`
      );

      if (!reportResponse.success) {
        setThrowErrorMessage(reportResponse.error);
        setThrowError(true);
      } else {
        const descr = generateDestructionReportPrintDesc(destructRequestId, isPickPrint
          ? islandscape
            ? `${CF_URLS.printReports.request.mdrVaultItemizedPickReport}`
            : `${CF_URLS.printReports.request.mdrVaultItemizedPickPotraitReport}`
          : `${CF_URLS.printReports.request.mdrVaultItemizedStdReport}`);
        const comment = destructionPickReportComment(reqBody);
        await insertLoggedEvent({
          comment: comment,
          description: descr,
          operationTypeId: operationTypes.update
        });
        fetchDataExistingMDR(destructRequestId);
      }
    } catch (e) {
      const { refId, message } = handleRefId(
        e.message || findErrorMessage(ERROR_TYPES.ISSUE)
      );
      dataDogErrorLog(message, "", { error: e }, refId);
      setThrowErrorMessage(message);
      setThrowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setThrowError(false);
    setIsVisible(false);
    setIsPickPrint(false);
  };

  return (
    <DownloadPopup
      title={t("common.downloadReport")}
      showPrintModal={isVisible}
      onCancleClick={handleCancel}
      onDownloadClick={handleDownload}
      setSelectedValue={setSelectedValue}
      setThrowError={setThrowError}
      throwError={throwError}
      throwErrorMessage={throwErrorMessage}
      cfUrl={reportCUrl}
    />
  );
}
